/* Collection grid reveal — Aborder Products.
   Same safety contract as assets/homepage-reveal.js.

   1. The CSS hidden state is gated on `.cl-reveal-ready` on <body>, which ONLY
      this file adds. No script, a parse error, or an early bail means nothing
      is ever hidden.
   2. Nothing inside a `.swiper` is observed — off-screen slides never
      intersect, so they would stay invisible forever.
   3. A viewport-scoped sweep plus a throttled scroll listener reveal anything
      the observer missed. The sweep is deliberately NOT a blanket reveal-all:
      that spends the animation before the reader arrives.
   4. Re-runs after a facet filter or a paginated load swaps the grid, because
      those replace .card-wrapper nodes that were never observed.

   Only the product grid animates. The collection hero is left alone: it is the
   first thing on screen and fading it in delays the page's own title. */
(function () {
  'use strict';

  var FAILSAFE_MS = 2500;
  var STAGGER_MS = 55;
  var STAGGER_CAP = 7;

  /* BOTH COLLECTION SURFACES, not just one.

     This started as the product grid on a single collection page. The
     collections INDEX — /collections, the "view all collections" page — is a
     grid of the same kind and had no reveal at all, so it appeared fully
     formed while every neighbouring page faded in. Same script, same timing,
     one more body class. */
  function isCollection() {
    if (!document.body) return false;
    return (
      document.body.classList.contains('template-collection') ||
      document.body.classList.contains('template-list-collections')
    );
  }

  function revealAll() {
    var hidden = document.querySelectorAll('.cl-reveal:not(.cl-reveal--in)');
    for (var i = 0; i < hidden.length; i++) hidden[i].classList.add('cl-reveal--in');
  }

  function sweepVisible() {
    var vh = window.innerHeight || document.documentElement.clientHeight || 0;
    if (!vh) {
      revealAll();
      return;
    }
    var hidden = document.querySelectorAll('.cl-reveal:not(.cl-reveal--in)');
    for (var i = 0; i < hidden.length; i++) {
      var r = hidden[i].getBoundingClientRect();
      if (r.top < vh && r.bottom > 0) hidden[i].classList.add('cl-reveal--in');
    }
  }

  var observer = null;

  function getObserver() {
    if (observer) return observer;
    observer = new IntersectionObserver(
      function (entries, obs) {
        for (var i = 0; i < entries.length; i++) {
          if (!entries[i].isIntersecting) continue;
          entries[i].target.classList.add('cl-reveal--in');
          obs.unobserve(entries[i].target);
        }
      },
      { rootMargin: '0px 0px -5% 0px', threshold: 0.05 }
    );
    return observer;
  }

  function collect() {
    /* Product cards on a collection page, collection tiles on the index. The
       two never appear together, so one query covering both is enough. */
    var all = document.querySelectorAll(
      '.collection-product .card-wrapper, .template-list-collections .categories-list__item'
    );
    var items = [];
    for (var i = 0; i < all.length; i++) {
      var el = all[i];
      if (el.classList.contains('cl-reveal')) continue; /* already handled */
      if (el.classList.contains('swiper-slide')) continue;
      if (el.closest('.swiper')) continue;
      items.push(el);
    }
    return items;
  }

  function wire() {
    if (!isCollection()) return;
    if (
      !('IntersectionObserver' in window) ||
      !Element.prototype.closest ||
      (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches)
    ) {
      return; /* `.cl-reveal-ready` never added => CSS hidden state never applies */
    }

    var items = collect();
    if (!items.length) return;

    document.body.classList.add('cl-reveal-ready');
    var obs = getObserver();
    var vh = window.innerHeight || 0;

    for (var i = 0; i < items.length; i++) {
      items[i].classList.add('cl-reveal');
      /* Stagger by position within this batch, capped so a 48-product grid
         does not end up with a two-second delay on its last card. */
      items[i].style.setProperty(
        '--cl-reveal-delay',
        Math.min(i % STAGGER_CAP, STAGGER_CAP) * STAGGER_MS + 'ms'
      );
      obs.observe(items[i]);

      /* Anything already on screen reveals next frame rather than waiting for
         a scroll that may never come on a short collection. */
      if (items[i].getBoundingClientRect().top < vh) {
        (function (el) {
          requestAnimationFrame(function () {
            requestAnimationFrame(function () {
              el.classList.add('cl-reveal--in');
            });
          });
        })(items[i]);
      }
    }

    setTimeout(sweepVisible, FAILSAFE_MS);
  }

  var scrollBound = false;
  function attachScrollBackstop() {
    if (scrollBound) return;
    scrollBound = true;
    var last = 0;
    function onScroll() {
      var now = Date.now();
      if (now - last < 120) return;
      last = now;
      sweepVisible();
    }
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
  }

  function init() {
    try {
      wire();
      attachScrollBackstop();
    } catch (e) {
      if (document.body) document.body.classList.remove('cl-reveal-ready');
      revealAll();
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  /* Facet filtering and pagination replace the grid's markup without a page
     load. The new cards have never been observed, so without this they would
     simply appear — which is acceptable — but re-wiring keeps them consistent
     with the first paint. Guarded by the same try/catch via init(). */
  document.addEventListener('shopify:section:load', init);
  window.addEventListener('popstate', init);

  if (window.MutationObserver) {
    var grid = document.querySelector('.collection-product');
    if (grid) {
      var pending = null;
      new MutationObserver(function () {
        if (pending) clearTimeout(pending);
        /* Debounced: a facet update fires many mutations in a burst. */
        pending = setTimeout(init, 120);
      }).observe(grid, { childList: true, subtree: true });
    }
  }
})();
